import React, { useState } from 'react'
import type { Language } from '../types/content'

export type TimeWindow = '' | 'morning' | 'afternoon' | 'evening'

export interface ContactFormValues {
  firstName: string
  lastName: string
  email: string
  phone: string
  service: string
  needs: string
  language: Language
  preferredDate: string
  preferredTime: TimeWindow
}

export type ErrorField = 'firstName' | 'lastName' | 'email' | 'phone' | 'service'
export type ContactFormErrors = Partial<Record<ErrorField, 'required' | 'invalid'>>
export type ContactFormStatus = 'idle' | 'submitting' | 'success' | 'error'

const errorOrder: ErrorField[] = ['firstName', 'lastName', 'email', 'phone', 'service']

export function useContactForm(defaultLanguage: Language, idPrefix: string) {
  const initial: ContactFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    needs: '',
    language: defaultLanguage,
    preferredDate: '',
    preferredTime: '',
  }
  const [values, setValues] = useState<ContactFormValues>(initial)
  const [errors, setErrors] = useState<ContactFormErrors>({})
  const [status, setStatus] = useState<ContactFormStatus>('idle')

  function update<K extends keyof ContactFormValues>(key: K, value: ContactFormValues[K]) {
    setValues((v) => ({ ...v, [key]: value }))
    if ((errorOrder as string[]).includes(key)) {
      setErrors((e) => {
        const next = { ...e }
        delete next[key as ErrorField]
        return next
      })
    }
  }

  function validate(): ContactFormErrors {
    const next: ContactFormErrors = {}
    if (!values.firstName.trim()) next.firstName = 'required'
    if (!values.lastName.trim()) next.lastName = 'required'
    const email = values.email.trim()
    if (!email) next.email = 'required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'invalid'
    const digits = values.phone.replace(/\D/g, '')
    if (!digits) next.phone = 'required'
    else if (digits.length < 10) next.phone = 'invalid'
    if (!values.service) next.service = 'required'
    return next
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const found = validate()
    setErrors(found)
    const first = errorOrder.find((k) => found[k])
    if (first) {
      document.getElementById(`${idPrefix}-${first}`)?.focus()
      return
    }
    setStatus('submitting')
    try {
      await new Promise((resolve) => setTimeout(resolve, 1100))
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  function reset() {
    setValues(initial)
    setErrors({})
    setStatus('idle')
  }

  return { values, errors, status, update, submit, reset }
}
